import { z } from 'zod';
/**
 * Orchestrator (BP-16) request/response shapes, shared by the API (validation) and the web app (types).
 * This slice is the READ-ONLY ask: a question scoped to one live module, answered from the caller's own
 * org data. The response exposes the cost tier but never the model name (Master Spec section 7).
 */
/** Modules the read-only Orchestrator can ground on in this slice. */
export const orchestratorModuleSchema = z.enum(['suppliers', 'categories', 'spend', 'contracts', 'savings']);
export const askRequestSchema = z
    .object({
    // A module slug from ORCHESTRATOR_MODULES (any Column-1 module), not only the persisted few.
    module: z.string().trim().min(1).max(64),
    question: z.string().trim().min(1).max(2000),
})
    .strict();
export const askSourceSchema = z.object({
    module: z.string(),
    id: z.string(),
    label: z.string(),
});
export const askUsageSchema = z.object({
    tier: z.enum(['S', 'M', 'L']),
    credits: z.number(),
    grounded: z.number(),
    injectionFlagged: z.boolean(),
    modelUnavailable: z.boolean(),
});
/**
 * A drafted write the Orchestrator PREPARES from the user's request (D-013/BP-16: AI prepares, the
 * human confirms). It is never applied by the AI; the UI previews it and, on confirm, calls the
 * module's existing audited create endpoint. `payload` is already validated server-side against that
 * module's create schema before it leaves the gateway.
 */
export const askDraftSchema = z.object({
    module: z.string(),
    summary: z.string(),
    fields: z.array(z.object({ label: z.string(), value: z.string() })),
    payload: z.record(z.unknown()),
});
export const askResponseSchema = z.object({
    answer: z.string(),
    sources: z.array(askSourceSchema),
    followups: z.array(z.string()),
    usage: askUsageSchema,
    draft: askDraftSchema.nullable(),
});
/** GET /orchestrator/credits — current-month AI usage vs the org's monthly allowance (BP-16). */
export const creditBalanceSchema = z.object({
    used: z.number(),
    allowance: z.number(),
    remaining: z.number(),
    pct: z.number(),
    warn: z.boolean(),
    over: z.boolean(),
});
/** POST /orchestrator/feedback — thumbs up/down on an answer (BP-16; feeds reuse ranking). */
export const feedbackRequestSchema = z
    .object({
    module: z.string().trim().min(1).max(64),
    question: z.string().trim().min(1).max(2000),
    answer: z.string().trim().min(1).max(8000),
    rating: z.union([z.literal(1), z.literal(-1)]),
})
    .strict();
//# sourceMappingURL=orchestrator.js.map